import React from "react";
import Switch from "../../../form/Switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  privacyDetails,
  privacyUpdate,
} from "../../../../services/privacyService";
import { toast } from "react-toastify";
import Input from "../../../form/Input";
import Form from "../../../form/Form";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";

const Auth2FA = () => {
  const queryClient = useQueryClient();
  const { values, handleChange, handleSubmit, errors, setFieldsValue } =
    useForm();

  const { data: privacyData, isLoading } = useQuery({
    queryKey: ["provacy-security"],
    queryFn: async () => {
      const res = await privacyDetails();
      if (res?.status === 200) {
        setFieldsValue(res?.data);
      }
      return res;
    },
  });

  const { mutate: privacyControlHandler, isPending: updatePending } =
    useMutation({
      mutationFn: async (val) => {
        const res = await privacyUpdate({
          id: values?._id,
          privacyControl: {
            ...values?.privacyControl,
            ...val?.privacyControl,
          },
        });

        return { res, payload: val };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          toast.warning(res?.message);
          setFieldsValue(privacyData?.data);
        }
      },
    });

  return (
    <Form onSubmit={privacyControlHandler} handleSubmit={handleSubmit}>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch
            checked={values?.privacyControl?.userTwoFactor}
            onChange={(e) => {
              setFieldsValue({
                privacyControl: {
                  ...values?.privacyControl,
                  userTwoFactor: e.target.checked,
                },
              });
            }}
            name={"userTwoFactor"}
            disabled={isLoading}
          />
          <div className="space-y-[2px]">
            <div className="font-semibold text-secondary">
              Multi-Factor Email Authentication (2FA)
            </div>
            <div className="text-xs font-medium text-text-secondary">
              Require email 2FA and set the duration for which devices can be
              remembered
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={values?.privacyControl?.adminTwoFactor}
            onChange={(e) => {
              setFieldsValue({
                privacyControl: {
                  ...values?.privacyControl,
                  adminTwoFactor: e.target.checked,
                },
              });
            }}
            name={"adminTwoFactor"}
            disabled={isLoading}
          />
          <div className="space-y-[2px]">
            <div className="font-semibold text-secondary">
              Disable Email (2FA) For Admin Login
            </div>
            <div className="text-xs font-medium text-text-secondary">
              Disabe 2-factor authentication for admin login security
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="whitespace-nowrap">Email 2FA is required after</span>
          <span className="flex w-16">
            <Input
              inputType={"number"}
              value={values?.privacyControl?.twoFactorAfter || 0}
              inputPlaceholder={"days"}
              onChange={(e) => {
                setFieldsValue({
                  privacyControl: {
                    ...values?.privacyControl,
                    twoFactorAfter: Number(e.target.value),
                  },
                });
              }}
            />
          </span>
          <span className="whitespace-nowrap">days without login.</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="whitespace-nowrap">Resend OTP will disable for</span>
          <span className="flex w-24">
            <Input
              inputType={"number"}
              value={values?.privacyControl?.otpDisabledFor || 0}
              inputPlaceholder={"second(s)"}
              onChange={(e) => {
                setFieldsValue({
                  privacyControl: {
                    ...values?.privacyControl,
                    otpDisabledFor: Number(e.target.value),
                  },
                });
              }}
            />
          </span>
          <span className="whitespace-nowrap">
            second(s) after each request.
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={values?.privacyControl?.otpBlock?.status}
            onChange={(e) => {
              setFieldsValue({
                privacyControl: {
                  ...values?.privacyControl,
                  otpBlock: {
                    ...values?.privacyControl?.otpBlock,
                    status: e.target.checked,
                  },
                },
              });
            }}
            name={"userTwoFactor"}
            disabled={isLoading}
          />
          <div className="space-y-[2px]">
            <div className="font-semibold text-secondary">
              Enable OTP block on failed 2FA attempts
            </div>
            <div className="text-xs font-medium text-text-secondary">
              Block otp failed after multiple failed 2FA requests.
            </div>
          </div>
        </div>
        {values?.privacyControl?.otpBlock?.status && (
          <div className="flex items-center gap-1">
            <span className="whitespace-nowrap">Disabled verification for</span>
            <span className="flex w-24">
              <Input
                inputType={"number"}
                value={values?.privacyControl?.otpBlock?.duration || 0}
                inputPlaceholder={"minuts(s)"}
                onChange={(e) => {
                  setFieldsValue({
                    privacyControl: {
                      ...values?.privacyControl,
                      otpBlock: {
                        ...values?.privacyControl?.otpBlock,
                        duration: Number(e.target.value),
                      },
                    },
                  });
                }}
              />
            </span>
            <span className="whitespace-nowrap">minuts(s) after</span>
            <span className="flex w-24">
              <Input
                inputType={"number"}
                value={values?.privacyControl?.otpBlock?.attempt || 0}
                inputPlaceholder={"failed attempt(s)"}
                onChange={(e) => {
                  setFieldsValue({
                    privacyControl: {
                      ...values?.privacyControl,
                      otpBlock: {
                        ...values?.privacyControl?.otpBlock,
                        attempt: Number(e.target.value),
                      },
                    },
                  });
                }}
              />
            </span>
            <span className="whitespace-nowrap">failed attempt(s).</span>
          </div>
        )}
      </div>
      <div>
        <Button
          buttonType="submit"
          buttonLabel={"Update Security"}
          loading={updatePending}
          isDisable={updatePending}
        />
      </div>
    </Form>
  );
};

export default Auth2FA;
